// Real WebRTC Teleconsultation Component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import TeleconsultationChat from '../../components/TeleconsultationChat';
import { 
  Video, VideoOff, Mic, MicOff, Phone, PhoneOff, 
  Monitor, MonitorOff, MessageSquare, Settings, 
  User, Clock, AlertTriangle, CheckCircle, Loader2,
  Maximize2, Minimize2, RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WebRTC configuration
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' }
  ]
};

const Teleconsultation = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  
  // Extract userId from user object
  const userId = user?.id;
  
  // Room state
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Call state
  const [callStatus, setCallStatus] = useState('idle'); // idle, connecting, connected, ended
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Media state
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [remoteConnected, setRemoteConnected] = useState(false);
  
  // Device state
  const [devices, setDevices] = useState({ video: [], audio: [] });
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  
  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  
  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const timerRef = useRef(null);
  const containerRef = useRef(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch room info
  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/teleconsultation/room/${roomId}`, { headers });
        setRoom(res.data);
      } catch (err) {
        setError(t('teleconsultation.room_not_found'));
        toast.error(t('teleconsultation.room_not_found'));
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
  }, [roomId]);

  // Get available devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        setDevices({
          video: deviceList.filter(d => d.kind === 'videoinput'),
          audio: deviceList.filter(d => d.kind === 'audioinput')
        });
      } catch (err) {
        console.error('Error getting devices:', err);
      }
    };
    getDevices();
  }, []);

  // Timer for call duration
  useEffect(() => {
    if (callStatus === 'connected') {
      timerRef.current = setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      endCall();
    };
  }, []);

  // Format duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start local media stream
  const startLocalStream = async () => {
    try {
      const constraints = {
        video: selectedVideoDevice 
          ? { deviceId: { exact: selectedVideoDevice } }
          : { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
        audio: selectedAudioDevice
          ? { deviceId: { exact: selectedAudioDevice } }
          : { echoCancellation: true, noiseSuppression: true }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      return stream;
    } catch (err) {
      console.error('Error accessing media:', err);
      if (err.name === 'NotAllowedError') {
        toast.error(t('teleconsultation.allow_camera_mic'));
      } else if (err.name === 'NotFoundError') {
        toast.error(t('teleconsultation.no_device_found'));
      } else {
        toast.error(t('teleconsultation.device_error'));
      }
      return null;
    }
  };

  // Create peer connection
  const createPeerConnection = () => {
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    pc.onicecandidate = (event) => {
      if (event.candidate && remoteUserId) {
        // Send ICE candidate via signaling
        sendSignalingMessage({
          type: 'ice_candidate',
          target_id: remoteUserId,
          candidate: event.candidate
        });
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected') {
        setRemoteConnected(true);
        setCallStatus('connected');
      } else if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        setRemoteConnected(false);
        toast.warning('Connexion instable...');
      }
    };
    
    pc.ontrack = (event) => {
      console.log('Remote track received');
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
        setRemoteConnected(true);
      }
    };
    
    return pc;
  };

  // WebSocket connection for signaling
  const wsRef = useRef(null);
  const [remoteUserId, setRemoteUserId] = useState(null);

  // Connect to WebSocket signaling server
  const connectSignaling = useCallback(() => {
    if (!roomId || !token) return null;
    
    const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/webrtc/${roomId}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('WebRTC signaling connected');
    };
    
    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      console.log('Signaling message:', data.type);
      
      switch (data.type) {
        case 'room_info':
          // Store ICE servers and participants
          if (data.participants?.length > 1) {
            const other = data.participants.find(p => p.user_id !== userId);
            if (other) setRemoteUserId(other.user_id);
          }
          break;
          
        case 'user_joined':
          setRemoteUserId(data.user_id);
          toast.success(`${data.user_name} ${t('teleconsultation.participant_joined')}`);
          // If we're already in call, send offer to new user
          if (peerConnectionRef.current && callStatus === 'connected') {
            await sendOffer(data.user_id);
          }
          break;
          
        case 'user_left':
          toast.info(t('teleconsultation.participant_left'));
          setRemoteConnected(false);
          setRemoteUserId(null);
          break;
          
        case 'offer':
          await handleOffer(data.offer, data.sender_id);
          break;
          
        case 'answer':
          await handleAnswer(data.answer);
          break;
          
        case 'ice_candidate':
          await handleIceCandidate(data.candidate);
          break;
          
        case 'call_started':
          if (data.initiator_id !== userId) {
            toast.info('Appel entrant...');
          }
          break;
          
        case 'call_ended':
          toast.info('Appel terminé');
          endCall();
          break;
          
        case 'media_state':
          // Handle remote media state changes
          console.log('Remote media state:', data);
          break;
          
        default:
          break;
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    ws.onclose = () => {
      console.log('WebSocket closed');
    };
    
    return ws;
  }, [roomId, token, userId, callStatus]);

  // Initialize WebSocket on mount
  useEffect(() => {
    if (roomId && token) {
      wsRef.current = connectSignaling();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [roomId, token, connectSignaling]);

  // Send signaling message
  const sendSignalingMessage = (message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  // Send offer to remote peer
  const sendOffer = async (targetId) => {
    if (!peerConnectionRef.current) return;
    
    const offer = await peerConnectionRef.current.createOffer();
    await peerConnectionRef.current.setLocalDescription(offer);
    
    sendSignalingMessage({
      type: 'offer',
      target_id: targetId,
      offer: offer
    });
  };

  // Handle incoming offer
  const handleOffer = async (offer, senderId) => {
    if (!peerConnectionRef.current) {
      const pc = createPeerConnection();
      peerConnectionRef.current = pc;
      
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          pc.addTrack(track, localStreamRef.current);
        });
      }
    }
    
    await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await peerConnectionRef.current.createAnswer();
    await peerConnectionRef.current.setLocalDescription(answer);
    
    sendSignalingMessage({
      type: 'answer',
      target_id: senderId,
      answer: answer
    });
    
    setCallStatus('connected');
  };

  // Handle incoming answer
  const handleAnswer = async (answer) => {
    if (peerConnectionRef.current) {
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
      setCallStatus('connected');
      toast.success(t('teleconsultation.connection_established'));
    }
  };

  // Handle incoming ICE candidate
  const handleIceCandidate = async (candidate) => {
    if (peerConnectionRef.current && candidate) {
      try {
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.error('Error adding ICE candidate:', e);
      }
    }
  };

  // Start call
  const startCall = async () => {
    setCallStatus('connecting');
    
    const stream = await startLocalStream();
    if (!stream) {
      setCallStatus('idle');
      return;
    }
    
    const pc = createPeerConnection();
    peerConnectionRef.current = pc;
    
    // Add local tracks to peer connection
    stream.getTracks().forEach(track => {
      pc.addTrack(track, stream);
    });
    
    // Notify via signaling that we're starting call
    sendSignalingMessage({ type: 'call_start' });
    
    // If remote user is already in room, send offer
    if (remoteUserId) {
      await sendOffer(remoteUserId);
    } else {
      // Wait for remote user to join
      toast.info(t('teleconsultation.waiting_participant'));
      setCallStatus('waiting');
    }
  };

  // End call
  const endCall = useCallback(() => {
    // Notify via signaling
    sendSignalingMessage({ type: 'call_end' });
    
    // Stop all tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    // Clear video elements
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    
    // Clear timer
    if (timerRef.current) clearInterval(timerRef.current);
    
    setCallStatus('ended');
    setRemoteConnected(false);
    setScreenSharing(false);
    setRemoteUserId(null);
    
    // Notify backend
    if (roomId && token) {
      axios.post(`${API_URL}/api/teleconsultation/room/${roomId}/leave`, { duration }, { headers })
        .catch(console.error);
    }
  }, [roomId, duration, token]);

  // Toggle video
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };

  // Toggle audio
  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
      }
    }
  };

  // Toggle screen sharing
  const toggleScreenShare = async () => {
    if (screenSharing) {
      // Stop screen sharing
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }
      
      // Replace with camera
      if (peerConnectionRef.current && localStreamRef.current) {
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender && videoTrack) {
          sender.replaceTrack(videoTrack);
        }
      }
      
      setScreenSharing(false);
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: 'always' },
          audio: false
        });
        
        screenStreamRef.current = screenStream;
        
        // Replace video track with screen
        if (peerConnectionRef.current) {
          const screenTrack = screenStream.getVideoTracks()[0];
          const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            sender.replaceTrack(screenTrack);
          }
        }
        
        // Update local preview
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }
        
        // Handle screen share stop
        screenStream.getVideoTracks()[0].onended = () => {
          toggleScreenShare();
        };
        
        setScreenSharing(true);
        toast.success(t('teleconsultation.screen_share_enabled'));
      } catch (err) {
        console.error('Screen share error:', err);
        if (err.name !== 'NotAllowedError') {
          toast.error(t('teleconsultation.screen_share'));
        }
      }
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Reconnect
  const reconnect = async () => {
    endCall();
    setDuration(0);
    setTimeout(startCall, 500);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-slate-900' : 'bg-gray-100'}`}>
        <Card className={`max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : ''}`}>{error}</h3>
            <Button onClick={() => navigate(-1)}>{t('back')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-gray-900'}`}
      data-testid="teleconsultation-room"
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4 bg-gradient-to-b from-black/70 to-transparent">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/50">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
              {callStatus === 'connected' ? t('teleconsultation.in_consultation') : callStatus === 'connecting' ? t('teleconsultation.connecting') : t('teleconsultation.ready')}
            </Badge>
            {callStatus === 'connected' && (
              <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/50">
                <Clock className="w-3 h-3 mr-1" />
                {formatDuration(duration)}
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              className="text-white hover:bg-white/10"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(!showSettings)}
              className="text-white hover:bg-white/10"
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Video Grid */}
      <div className="relative h-screen">
        {/* Remote Video (Main) */}
        <div className="absolute inset-0 flex items-center justify-center">
          {remoteConnected ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-white/70">
              <div className="w-32 h-32 rounded-full bg-slate-700 flex items-center justify-center mb-4">
                <User className="w-16 h-16 text-slate-500" />
              </div>
              <p className="text-lg">{t('teleconsultation.waiting_participant')}</p>
              {callStatus === 'idle' && (
                <Button onClick={startCall} className="mt-4 bg-green-600 hover:bg-green-700">
                  <Video className="w-5 h-5 mr-2" />
                  {t('teleconsultation.start_consultation')}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Local Video (PiP) */}
        {callStatus !== 'idle' && (
          <div className="absolute bottom-24 right-4 w-48 h-36 rounded-xl overflow-hidden shadow-2xl border-2 border-white/20 z-10">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className={`w-full h-full object-cover ${!videoEnabled ? 'hidden' : ''}`}
            />
            {!videoEnabled && (
              <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                <VideoOff className="w-8 h-8 text-slate-500" />
              </div>
            )}
            <div className="absolute bottom-2 left-2 text-xs text-white bg-black/50 px-2 py-1 rounded">
              {t('teleconsultation.you')} {screenSharing && `(${t('teleconsultation.screen')})`}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center justify-center gap-3 max-w-lg mx-auto">
          {/* Mic Toggle */}
          <Button
            variant={audioEnabled ? 'secondary' : 'destructive'}
            size="lg"
            onClick={toggleAudio}
            className={`rounded-full w-14 h-14 ${audioEnabled ? 'bg-slate-700 hover:bg-slate-600' : ''}`}
            disabled={callStatus === 'idle'}
            data-testid="toggle-audio"
          >
            {audioEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
          </Button>

          {/* Video Toggle */}
          <Button
            variant={videoEnabled ? 'secondary' : 'destructive'}
            size="lg"
            onClick={toggleVideo}
            className={`rounded-full w-14 h-14 ${videoEnabled ? 'bg-slate-700 hover:bg-slate-600' : ''}`}
            disabled={callStatus === 'idle'}
            data-testid="toggle-video"
          >
            {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
          </Button>

          {/* Screen Share */}
          <Button
            variant={screenSharing ? 'default' : 'secondary'}
            size="lg"
            onClick={toggleScreenShare}
            className={`rounded-full w-14 h-14 ${screenSharing ? 'bg-blue-600' : 'bg-slate-700 hover:bg-slate-600'}`}
            disabled={callStatus !== 'connected'}
            data-testid="toggle-screen"
          >
            {screenSharing ? <MonitorOff className="w-6 h-6" /> : <Monitor className="w-6 h-6" />}
          </Button>

          {/* End Call */}
          {callStatus !== 'idle' ? (
            <Button
              variant="destructive"
              size="lg"
              onClick={endCall}
              className="rounded-full w-16 h-16 bg-red-600 hover:bg-red-700"
              data-testid="end-call"
            >
              <PhoneOff className="w-7 h-7" />
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={startCall}
              className="rounded-full w-16 h-16 bg-green-600 hover:bg-green-700"
              data-testid="start-call"
            >
              <Phone className="w-7 h-7" />
            </Button>
          )}

          {/* Reconnect */}
          {callStatus === 'connected' && (
            <Button
            variant="secondary"
            size="lg"
            onClick={reconnect}
            className="rounded-full w-14 h-14 bg-slate-700 hover:bg-slate-600"
            title={t('teleconsultation.reconnect')}
          >
              <RotateCcw className="w-6 h-6" />
            </Button>
          )}
        </div>

        {/* Call Status Message */}
        {callStatus === 'connecting' && (
          <div className="text-center mt-4">
            <p className="text-white/70 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('teleconsultation.connecting')}
            </p>
          </div>
        )}

        {callStatus === 'ended' && (
          <div className="text-center mt-4">
            <p className="text-white/70">{t('teleconsultation.ended')} • {t('teleconsultation.duration')}: {formatDuration(duration)}</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-2">
              {t('teleconsultation.back_dashboard')}
            </Button>
          </div>
        )}
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="absolute top-16 right-4 z-30 w-80 bg-slate-800 rounded-xl border border-slate-700 shadow-2xl p-4">
          <h3 className="text-white font-semibold mb-4">{t('teleconsultation.settings')}</h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-slate-400 text-sm block mb-2">{t('teleconsultation.camera')}</label>
              <select
                value={selectedVideoDevice}
                onChange={(e) => setSelectedVideoDevice(e.target.value)}
                className="w-full bg-slate-700 text-white border-slate-600 rounded-lg p-2"
              >
                {devices.video.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `${t('teleconsultation.camera')} ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-slate-400 text-sm block mb-2">{t('teleconsultation.microphone')}</label>
              <select
                value={selectedAudioDevice}
                onChange={(e) => setSelectedAudioDevice(e.target.value)}
                className="w-full bg-slate-700 text-white border-slate-600 rounded-lg p-2"
              >
                {devices.audio.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `${t('teleconsultation.microphone')} ${device.deviceId.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
      
      {/* Chat Component */}
      {roomId && (
        <TeleconsultationChat 
          roomId={roomId}
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          onToggle={() => setChatOpen(!chatOpen)}
        />
      )}
    </div>
  );
};

export default Teleconsultation;
